import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  ShieldCheck,
  Users,
  Wallet,
} from "lucide-react";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import NoIndexSEO from "../../components/SEO/NoIndexSEO";
import mentorshipService from "../../api/mentorshipService";
import useMediaQuery from "../../hooks/useMediaQuery";
import { SESSION_MINUTES, SESSION_PRICE } from "../../data/mentorshipData";
import BookingPanel from "./components/BookingPanel";
import CategoryFilter from "./components/CategoryFilter";
import ConfirmedCard from "./components/ConfirmedCard";
import FoundersCard from "./components/FoundersCard";
import MentorCard from "./components/MentorCard";
import MentorProfile from "./components/MentorProfile";
import { firstName } from "./components/MentorParts";
import { toMentorViewModel } from "./mentorshipAdapter";
import { apiErrorMessage, groupSlotsByDate, isValidEmail } from "./mentorshipUtils";
import "./Mentorship.scss";

const DISCLAIMER =
  "Mentors share personal experience and education. This is not licensed financial, tax, legal, or job-placement advice, and no mentor can guarantee a job at any hospital.";

const CONFIRM_POLL_MS = 2000;
const CONFIRM_MAX_POLLS = 10;
const MENTORS_PER_PAGE = 9;

const Mentorship = () => {
  const isDesktop = useMediaQuery("(min-width: 992px)");
  const rootRef = useRef(null);
  const didMount = useRef(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const authUser = useSelector((state) => state.auth?.user);

  // What Stripe sent us back with (read once, then stripped from the URL).
  const returned = useRef({
    sessionId: searchParams.get("session_id"),
    cancelled: searchParams.get("booking") === "cancelled",
  });
  const urlCleaned = useRef(false);

  const [mentors, setMentors] = useState([]);
  const [mentorsStatus, setMentorsStatus] = useState("loading"); // loading | ready | error
  const [category, setCategory] = useState("All");
  const [mentorPage, setMentorPage] = useState(1);
  const [screen, setScreen] = useState(
    returned.current.sessionId ? "confirmed" : "home",
  ); // home | profile | booking | confirmed
  const [mentorId, setMentorId] = useState(null);

  const [availability, setAvailability] = useState({
    mentorId: null,
    status: "idle", // idle | loading | ready | error
    slots: [],
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [topic, setTopic] = useState("");
  const [nurseName, setNurseName] = useState("");
  const [nurseEmail, setNurseEmail] = useState("");
  const [nursePhone, setNursePhone] = useState("");
  const contactPrefilled = useRef(false);
  const [agreed, setAgreed] = useState(false);
  const [paying, setPaying] = useState(false);

  const [confirmation, setConfirmation] = useState(
    returned.current.sessionId ? { status: "processing" } : null,
  );

  const mentor = mentors.find((item) => item.id === mentorId) || null;
  const filteredMentors =
    category === "All"
      ? mentors
      : mentors.filter((item) => item.cats.includes(category));
  const mentorPageCount = Math.max(
    1,
    Math.ceil(filteredMentors.length / MENTORS_PER_PAGE),
  );
  const safeMentorPage = Math.min(mentorPage, mentorPageCount);
  const pagedMentors = filteredMentors.slice(
    (safeMentorPage - 1) * MENTORS_PER_PAGE,
    safeMentorPage * MENTORS_PER_PAGE,
  );
  const founders = useMemo(
    () => mentors.filter((item) => item.founder),
    [mentors],
  );

  const availabilityForMentor =
    availability.mentorId === mentorId
      ? availability
      : { status: "idle", slots: [] };
  const days = useMemo(
    () => groupSlotsByDate(availabilityForMentor.slots),
    [availabilityForMentor.slots],
  );

  // Desktop books inline next to the profile, so it never needs a separate
  // "booking" screen (e.g. if the window is widened mid-flow).
  const view = isDesktop && screen === "booking" ? "profile" : screen;

  /* ------------------------------ data ------------------------------ */

  const loadMentors = useCallback(async () => {
    setMentorsStatus("loading");
    try {
      const res = await mentorshipService.getMentors();
      setMentors((res.data?.data || []).map(toMentorViewModel));
      setMentorsStatus("ready");
    } catch (error) {
      setMentorsStatus("error");
    }
  }, []);

  useEffect(() => {
    loadMentors();
  }, [loadMentors]);

  const loadAvailability = useCallback(async (id) => {
    setAvailability({ mentorId: id, status: "loading", slots: [] });
    try {
      const res = await mentorshipService.getAvailability(id);
      setAvailability((prev) =>
        prev.mentorId === id
          ? { mentorId: id, status: "ready", slots: res.data?.data || [] }
          : prev,
      );
    } catch (error) {
      setAvailability((prev) =>
        prev.mentorId === id
          ? { mentorId: id, status: "error", slots: [] }
          : prev,
      );
    }
  }, []);

  useEffect(() => {
    if (mentorId !== null) loadAvailability(mentorId);
  }, [mentorId, loadAvailability]);

  /* ------------------- returning from Stripe Checkout ------------------- */

  // Drop ?session_id / ?booking from the URL so a refresh doesn't re-trigger.
  useEffect(() => {
    const { sessionId, cancelled } = returned.current;
    if (urlCleaned.current || (!sessionId && !cancelled)) return;
    urlCleaned.current = true;
    navigate(pathname, { replace: true });
    if (cancelled) {
      toast.info("Checkout was cancelled. You haven't been charged.");
    }
  }, [navigate, pathname]);

  // Paid: wait for our webhook to record the booking, then show the result.
  useEffect(() => {
    const { sessionId } = returned.current;
    if (!sessionId) return undefined;

    let stopped = false;
    let timer;
    let polls = 0;

    const poll = async () => {
      try {
        const res = await mentorshipService.confirmBooking(sessionId);
        if (stopped) return;
        const data = res.data?.data;
        if (data?.status === "processing" && polls < CONFIRM_MAX_POLLS) {
          polls += 1;
          timer = setTimeout(poll, CONFIRM_POLL_MS);
          return;
        }
        setConfirmation(
          data?.status === "processing" ? { status: "error" } : data,
        );
      } catch (error) {
        if (!stopped) setConfirmation({ status: "error" });
      }
    };
    poll();

    return () => {
      stopped = true;
      clearTimeout(timer);
    };
  }, []);

  // Prefill the contact fields from the account once it's loaded (it may not
  // be ready on the first render), without overwriting anything the nurse
  // has already typed on a later render.
  useEffect(() => {
    if (contactPrefilled.current || !authUser) return;
    contactPrefilled.current = true;
    const accountName = [authUser.first_name, authUser.last_name]
      .filter(Boolean)
      .join(" ")
      .trim();
    setNurseName(accountName || authUser.email || "");
    setNurseEmail(authUser.email || "");
    setNursePhone(
      authUser.phone ? `${authUser.country_code || ""}${authUser.phone}` : "",
    );
  }, [authUser]);

  /* ---------------------------- navigation ---------------------------- */

  // Bring the top of the page back into view whenever the step changes.
  useEffect(() => {
    if (!didMount.current) {
      didMount.current = true;
      return;
    }
    rootRef.current?.scrollIntoView({ block: "start" });
  }, [view, mentorId]);

  const openMentor = (id) => {
    setMentorId(id);
    setSelectedDate(null);
    setSelectedSlot(null);
    setTopic("");
    setAgreed(false);
    setScreen("profile");
  };

  const goHome = () => {
    setConfirmation(null);
    setScreen("home");
  };

  const selectCategory = (next) => {
    setCategory(next);
    setMentorPage(1);
    setScreen("home");
  };

  const goBack = () => setScreen(view === "booking" ? "profile" : "home");

  const selectDate = (date) => {
    setSelectedDate(date);
    setSelectedSlot(null);
  };

  /* ------------------------------ payment ------------------------------ */

  const contactValid =
    nurseName.trim() && nurseEmail.trim() && isValidEmail(nurseEmail);

  const handlePay = async () => {
    if (!mentor || !selectedSlot || !agreed || !contactValid || paying) return;
    setPaying(true);
    try {
      const res = await mentorshipService.bookMentor(mentor.id, {
        date: selectedSlot.date,
        start_time: selectedSlot.start_time,
        end_time: selectedSlot.end_time,
        topic: topic.trim(),
        nurse_name: nurseName.trim(),
        nurse_email: nurseEmail.trim(),
        nurse_phone: nursePhone.trim(),
      });
      window.location.assign(res.data.data.url);
    } catch (error) {
      setPaying(false);
      toast.error(
        apiErrorMessage(error, "We couldn't start checkout. Please try again."),
      );
      if (error?.response?.status === 409) {
        // Slot was taken (or mentor changed) — refresh what's open.
        setSelectedSlot(null);
        loadAvailability(mentor.id);
      }
    }
  };

  /* ------------------------------ pieces ------------------------------ */

  const bookingPanel = (stickyFooter) => (
    <BookingPanel
      mentor={mentor}
      days={days}
      availabilityStatus={availabilityForMentor.status}
      selectedDate={selectedDate}
      selectedSlot={selectedSlot}
      nurseName={nurseName}
      onNurseName={setNurseName}
      nurseEmail={nurseEmail}
      onNurseEmail={setNurseEmail}
      nursePhone={nursePhone}
      onNursePhone={setNursePhone}
      contactValid={contactValid}
      topic={topic}
      onTopic={setTopic}
      agreed={agreed}
      paying={paying}
      onDate={selectDate}
      onSlot={setSelectedSlot}
      onToggleAgree={() => setAgreed((prev) => !prev)}
      onPay={handlePay}
      stickyFooter={stickyFooter}
    />
  );

  const confirmedCard = confirmation && (
    <ConfirmedCard
      status={confirmation.status}
      mentorName={confirmation.mentor?.name}
      booking={confirmation.booking}
      onDone={goHome}
    />
  );

  const listHeader = (
    <div className="ms-list-head">
      <h2 className="ms-h2">Find a mentor</h2>
      <span className="ms-small">
        {filteredMentors.length} mentor
        {filteredMentors.length === 1 ? "" : "s"}
      </span>
    </div>
  );

  const mentorList = (className) =>
    filteredMentors.length === 0 ? (
      <div className="ms-card ms-empty">
        No mentors in this topic yet. Try another topic.
      </div>
    ) : (
      <>
        <div className={className}>
          {pagedMentors.map((item) => (
            <MentorCard key={item.id} mentor={item} onOpen={openMentor} />
          ))}
        </div>
        {mentorPageCount > 1 && (
          <div className="ms-pagination">
            <button
              type="button"
              className="ms-btn ms-btn--secondary ms-btn--md"
              disabled={safeMentorPage === 1}
              onClick={() => setMentorPage((prev) => prev - 1)}
            >
              <ChevronLeft size={16} aria-hidden="true" />
              Previous
            </button>
            <span className="ms-small">
              Page {safeMentorPage} of {mentorPageCount}
            </span>
            <button
              type="button"
              className="ms-btn ms-btn--secondary ms-btn--md"
              disabled={safeMentorPage === mentorPageCount}
              onClick={() => setMentorPage((prev) => prev + 1)}
            >
              Next
              <ChevronRight size={16} aria-hidden="true" />
            </button>
          </div>
        )}
      </>
    );

  const backButton = (
    <button type="button" className="ms-back" onClick={goBack}>
      <ChevronLeft size={18} aria-hidden="true" />
      {isDesktop ? "Back to mentors" : "Back"}
    </button>
  );

  const kpis = [
    { icon: Users, value: mentors.length, label: "Mentors available" },
    { icon: ShieldCheck, value: founders.length, label: "Founding mentors" },
    { icon: Clock, value: `${SESSION_MINUTES} min`, label: "1-on-1 video call" },
    {
      icon: Wallet,
      value: `$${SESSION_PRICE}`,
      label: "One-time, no subscription",
    },
  ];

  const stateBlock =
    mentorsStatus === "loading" ? (
      <div className="ms-card ms-state">
        <div className="spinner-border text-primary" role="status" />
        <p className="ms-small">Loading mentors…</p>
      </div>
    ) : mentorsStatus === "error" ? (
      <div className="ms-card ms-state">
        <p className="ms-body">We couldn&apos;t load mentors right now.</p>
        <button
          type="button"
          className="ms-btn ms-btn--secondary ms-btn--md"
          onClick={loadMentors}
        >
          Try again
        </button>
      </div>
    ) : null;

  const intro = (
    <p className="ms-body">
      Book a {SESSION_MINUTES}-minute 1-on-1 call with a nurse who&apos;s done
      it.
    </p>
  );

  /* ---------------------------- Desktop ---------------------------- */
  if (isDesktop) {
    return (
      <div className="ms-page ms-page--desktop" ref={rootRef}>
        <NoIndexSEO title="Mentorship | Map My Pay" />
        <div className="ms-layout">
          <div className="ms-side">
            <div className="ms-card ms-side__block">
              <div className="ms-label ms-eyebrow">Browse by topic</div>
              <CategoryFilter
                variant="list"
                mentors={mentors}
                active={category}
                onSelect={selectCategory}
              />
            </div>
            <FoundersCard founders={founders} onOpen={openMentor} />
            <p className="ms-small ms-note">{DISCLAIMER}</p>
          </div>

          <div className="ms-main">
            <div className="ms-header">
              <h1 className="ms-h1">Mentorship</h1>
              {intro}
            </div>

            {view === "confirmed" && confirmedCard && (
              <div className="ms-card ms-confirm-wrap">{confirmedCard}</div>
            )}

            {view === "home" &&
              (stateBlock || (
                <>
                  <div className="ms-kpis">
                    {kpis.map(({ icon: Icon, value, label }) => (
                      <div key={label} className="ms-card ms-kpi">
                        <span className="ms-kpi__icon">
                          <Icon size={18} aria-hidden="true" />
                        </span>
                        <div>
                          <div className="ms-kpi__value">{value}</div>
                          <div className="ms-small">{label}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {listHeader}
                  {mentorList("ms-grid")}
                </>
              ))}

            {view === "profile" && mentor && (
              <>
                {backButton}
                <div className="ms-detail">
                  <div className="ms-card ms-detail__profile">
                    <MentorProfile mentor={mentor} showAvailability={false} />
                  </div>
                  <div className="ms-card ms-panel">
                    <h2 className="ms-h2">Book with {firstName(mentor)}</h2>
                    <p className="ms-small ms-mb-16">
                      {SESSION_MINUTES} minutes · ${SESSION_PRICE} one-time
                    </p>
                    {bookingPanel(false)}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  /* ---------------------------- Mobile ----------------------------- */
  return (
    <div className="ms-page ms-page--mobile" ref={rootRef}>
      <NoIndexSEO title="Mentorship | Map My Pay" />

      {(view === "profile" || view === "booking") && mentor && backButton}

      {view === "confirmed" && confirmedCard}

      {view === "home" && (
        <>
          <h1 className="ms-h1">Mentorship</h1>
          <div className="ms-mt-4">{intro}</div>

          {stateBlock || (
            <>
              <div className="ms-mt-16">
                <FoundersCard founders={founders} onOpen={openMentor} />
              </div>

              <div className="ms-mt-24">{listHeader}</div>
              <CategoryFilter
                variant="chips"
                mentors={mentors}
                active={category}
                onSelect={setCategory}
              />
              {mentorList("ms-list")}
              <p className="ms-small ms-note">{DISCLAIMER}</p>
            </>
          )}
        </>
      )}

      {view === "profile" && mentor && (
        <MentorProfile
          mentor={mentor}
          days={days}
          availabilityStatus={availabilityForMentor.status}
          onBook={() => setScreen("booking")}
        />
      )}

      {view === "booking" && mentor && (
        <>
          <h1 className="ms-h1 ms-h1--sm">Book with {firstName(mentor)}</h1>
          <p className="ms-small ms-mt-4">
            {SESSION_MINUTES} minutes · ${SESSION_PRICE} one-time
          </p>
          <div className="ms-mt-20">{bookingPanel(true)}</div>
        </>
      )}
    </div>
  );
};

export default Mentorship;
